import { ChevronUp, X, Layers, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { getRegionDisplay } from "@/lib/regions";
import { SceneQuestions, visibleEntries, optionsFor } from "./SceneQuestions";

/**
 * A scene's questions, every field visible at once.
 *
 * Deliberately not a wizard stepper. That form exists for a guided first run;
 * Studio is a repeat-use tool where you answer, generate, change one thing and
 * generate again. A stepper (or a drawer) would mean re-navigating for every
 * tweak, and a conversational sequence would bury an early answer in
 * scrollback where it can't be edited.
 *
 * Options are tiles rather than dropdowns because that is what the data is:
 * the step type is `tile_select`, and every option carries an emoji and a
 * one-line description. Collapsing them into a <select> threw both away and
 * made a six-question setup read as six identical grey boxes labelled
 * "Choose…". You could not see what a scene was without opening every one.
 *
 * Two states, because the panel serves two moments:
 *   full — setting the scene up, when the canvas is empty anyway
 *   bar  — after generating, when the images matter and you want to change
 *          one thing without losing sight of them
 */

export interface SceneSummary {
  id: string;
  name: string;
  regionKey: string | null;
  config: any;
}

interface ScenePanelProps {
  scene: SceneSummary;
  answers: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onClear: () => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function ScenePanel({
  scene, answers, onChange, onClear, collapsed, onToggleCollapsed,
}: ScenePanelProps) {
  const entries = visibleEntries(scene.config, answers);
  const answeredCount = entries.filter((e) => answers[e.key]).length;
  const region = scene.regionKey ? getRegionDisplay(scene.regionKey) : null;

  const header = (
    <div className="flex items-center gap-2.5 min-w-0">
      <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Layers className="w-4 h-4" />
      </span>
      <span className="flex flex-col min-w-0">
        <span className="text-sm font-medium truncate leading-tight">{scene.name}</span>
        <span className="text-[11px] text-muted-foreground truncate leading-tight">
          {region && <>{region.emoji} {region.label} · </>}
          {answeredCount === entries.length
            ? `all ${entries.length} set`
            : `${answeredCount} of ${entries.length} set · rest left to the model`}
        </span>
      </span>
    </div>
  );

  /**
   * One visible control, labelled by what it does.
   *
   * It was a ghost button reading "Done" when expanded — low contrast, easy to
   * miss, and "Done" reads like saving when everything here saves as you go.
   * "Hide questions" and "Edit answers" each say what the click will do, and
   * an outline button is actually visible against the panel.
   *
   * The whole header row toggles as well, so the chevron is a hint rather than
   * the only target. Remove is kept apart from it — a control that discards
   * the scene should not sit inside the one that opens it.
   */
  const actions = (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-border bg-background text-xs font-medium group-hover/header:bg-muted transition-colors">
        {collapsed ? <Pencil className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        {collapsed ? "Edit answers" : "Hide questions"}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); onClear(); }}
        className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        aria-label="Remove scene"
        title="Remove this scene from the session"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  if (collapsed) {
    // Chosen answers as chips: the whole scene readable in one line, and each
    // chip names the field so an unfamiliar value ("Warm Hygge") still says
    // what it answers.
    const chosen = entries
      .map((e) => {
        const opts = optionsFor(e.field, answers);
        const sel = opts.find((o) => o.key === answers[e.key]);
        if (!sel) return null;
        return { key: e.key, label: e.label, emoji: sel.emoji, value: sel.label };
      })
      .filter(Boolean) as { key: string; label: string; emoji?: string; value: string }[];

    return (
      <div className="rounded-xl border border-border bg-card/70 backdrop-blur px-3 py-2.5 mb-2">
        <div
          role="button"
          tabIndex={0}
          onClick={onToggleCollapsed}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleCollapsed(); } }}
          aria-expanded={false}
          className="group/header flex items-center justify-between gap-2 cursor-pointer rounded-md -mx-1 px-1 py-0.5"
        >
          {header}
          {actions}
        </div>
        {chosen.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {chosen.map((c) => (
              <span
                key={c.key}
                title={c.label}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px]"
              >
                {c.emoji && <span aria-hidden>{c.emoji}</span>}
                <span className="text-muted-foreground">{c.label}:</span>
                <span className="font-medium">{c.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card/70 backdrop-blur mb-3 overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleCollapsed}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggleCollapsed(); } }}
        aria-expanded
        className="group/header flex items-center justify-between gap-2 px-4 py-3 border-b border-border/60 cursor-pointer"
      >
        {header}
        {actions}
      </div>

      <div className="p-4 max-h-[52vh] overflow-y-auto">
        <SceneQuestions entries={entries} answers={answers} onChange={onChange} />
      </div>
    </div>
  );
}

